import { Trash2 } from 'lucide-react';
import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { TableCell, TableRow } from '@/components/ui/table';
import { cn } from '@/lib/utils';
import { PlannedResourceRow } from 'components/IAP/hooks/assignmentListHooks';
import AssignmentCardResourceAttachmentDialog from './AssignmentCardResourceAttachmentDialog';
import Ics204aButtonSignatureProgress from './Ics204aButtonSignatureProgress';
import PlannedResourcePopoutButton from './PlannedResourcePopoutButton';
import { ICS204_DELETE_BTN, ICS204_OUTLINE_BTN } from './assignmentCardStyles';

const AssignmentCardPlannedResourceView = ({
  plannedResource,
  periodNumber,
  canEdit = false,
  disabled = false,
  onStartEdit,
  onDelete,
}: {
  plannedResource: PlannedResourceRow;
  periodNumber?: number;
  canEdit?: boolean;
  disabled?: boolean;
  onStartEdit?: () => void;
  onDelete?: () => void;
}) => {
  const [showDialog, setShowDialog] = useState(false);
  const has204a = !!plannedResource.has204a;
  const isDocumentationEntry = !!plannedResource.isDocumentationEntry;
  const rowEditable = canEdit && !!onStartEdit;

  return (
    <>
      <TableRow
        data-testid="ics204-planned-resource-row"
        className={cn(rowEditable && 'tw:cursor-pointer')}
        onClick={() => {
          if (rowEditable && !disabled) onStartEdit?.();
        }}
        onKeyDown={(e) => {
          if (!rowEditable || disabled) return;
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            onStartEdit?.();
          }
        }}
        role={rowEditable ? 'button' : undefined}
        tabIndex={rowEditable ? 0 : undefined}
      >
        <TableCell />
        <TableCell>{plannedResource.name}</TableCell>
        <TableCell>{plannedResource.leader}</TableCell>
        <TableCell>{plannedResource.numberOfPersons}</TableCell>
        <TableCell>{plannedResource.contactInfo}</TableCell>
        <TableCell>{plannedResource.reportingInfo}</TableCell>
        <TableCell>
          <div className="tw:flex tw:flex-row tw:items-center tw:justify-end tw:gap-2">
            {has204a ? (
              <Button
                type="button"
                size="sm"
                variant="outline"
                className={ICS204_OUTLINE_BTN}
                title="View or edit 204A details"
                data-testid="ics204-planned-resource-204a-button"
                onClick={(e) => {
                  e.stopPropagation();
                  setShowDialog(true);
                }}
              >
                <Ics204aButtonSignatureProgress
                  walaId={plannedResource.walaId}
                  plannedResourceKey={plannedResource.id}
                />
                204A
              </Button>
            ) : null}
            {/* Fixed-width slot so 204A stays aligned when popout is absent. */}
            {!isDocumentationEntry ? (
              <div
                onClick={(e) => e.stopPropagation()}
                onKeyDown={(e) => e.stopPropagation()}
              >
                <PlannedResourcePopoutButton
                  plannedResource={plannedResource}
                  periodNumber={periodNumber}
                />
              </div>
            ) : (
              <div className="tw:h-8 tw:w-8 tw:shrink-0" aria-hidden />
            )}
            {canEdit && onDelete ? (
              <Button
                type="button"
                variant="ghost"
                size="icon-sm"
                aria-label="Delete planned resource"
                className={cn(ICS204_DELETE_BTN, 'tw:shrink-0')}
                data-testid="ics204-edit-delete-resource"
                disabled={disabled}
                onClick={(e) => {
                  e.stopPropagation();
                  onDelete();
                }}
              >
                <Trash2 className="tw:h-4 tw:w-4" />
              </Button>
            ) : null}
          </div>
        </TableCell>
      </TableRow>
      {showDialog && has204a ? (
        <AssignmentCardResourceAttachmentDialog
          onClose={() => setShowDialog(false)}
          data={plannedResource}
          periodNumber={periodNumber}
        />
      ) : null}
    </>
  );
};

export default AssignmentCardPlannedResourceView;
